import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, Validators} from '@angular/forms';
import {combineLatest, Subscription} from 'rxjs';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {tap} from 'rxjs/operators';
import {PreviewTableComponent} from '../preview-table/preview-table.component';
import {MainPageComponent} from '../../../../../main/components/main/main-page.component';
import {IDataSourcePreview} from '../../interfaces/i-data-source-preview';
import { loadavg } from 'os';

@Component({
  selector: 'morva-web-service-edit',
  templateUrl: './web-service-create.component.html',
  styleUrls: ['./web-service-create.component.scss'],
})
export class WebServiceCreateComponent implements OnInit,OnDestroy {
  submitting: boolean;
  workspaceId: string;
  
  dataTypes = [{
    id: 0,
    name: 'CSV',
  },
  {
    id: 1,
    name: 'JSON',
  }];
  methodTypes = [{
    id: 0,
    name: 'GET',
  },
  {
    id: 1,
    name: 'POST',
  }];
  authenticationTypes = [{
    id: 0,
    name: 'Basic Authentication',
  },
  {
    id: 1,
    name: 'No Authentication',
  }];
  webServiceForm = this.formBuilder.group({
    name: ['', Validators.required],
    address: ['', Validators.required],
    dataType: ['JSON', Validators.required],
    methodType: ['GET', Validators.required],
    authenticationType: ['No Authentication', Validators.required],
    username: '',
    password: '',
  });
  requestParameters: {name: string; value: string; type: string }[] = [
    {
      name: '',
      value: '',
      type: '1',
    },
  ];
  requestHeaders: {name: string; value: string; }[] = [
    {
      name: '',
      value: '',
    },
  ];
  jsonBody: {name: string; value: string;}[] = [
    {
      name: '',
      value: '',
    },
  ];
  validParameters = false;
  observables = [];
  subscriptions: Subscription[] = [];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogRef: NbDialogRef<MainPageComponent>,
    @Inject('DataSourcePreview')private dataSourcePreviewFacade: IDataSourcePreview,
    private dialogService: NbDialogService,
  ) { }

  ngOnInit() {
    this.setSubscribers();
    this.conditionalUsernameAndPasswordValidator();
  }

  setSubscribers(): void {
    this.observables.push(this.dataSourcePreviewFacade.getWorkspaceId$().
    pipe(tap(workspaceId => this.workspaceId = workspaceId)));
    this.subscriptions.push(combineLatest(this.observables).subscribe());
    this.subscriptions.push(this.dataSourcePreviewFacade.getSubmitState$().subscribe(
      (submit) => {
        if(submit === 'submitted') {
          const closeOnBackdropClick = false;
          this.dialogService.open(PreviewTableComponent, {
            context: {workspaceId: this.workspaceId, type: 'CreateDataSource'},
            closeOnBackdropClick,
          });
          this.dataSourcePreviewFacade.setSubmitState('notSubmitted');
          this.dialogRef.close(true);
          this.submitting = false;
        } else if (submit === 'notSubmitted') {
          this.submitting = false;
        }
      },
    ));
  }

  addParameter(): void {
    this.validParameters = false;
    this.requestParameters.push({
      name: '',
      value: '',
      type: '1',
    });
  }

  removeParameter(parameter: any): void {
    if(this.requestParameters.length > 1) {
      this.requestParameters = this.requestParameters.filter(p => p !== parameter);
      this.validatorRequestParameters();
    }
  }
  addHeader(): void {
    this.validParameters = false;
    this.requestHeaders.push({
      name: '',
      value: '',
    });
  }

  removeHeader(header: any): void {
    if(this.requestHeaders.length > 1) {
      this.requestHeaders = this.requestHeaders.filter(h => h !== header);
      this.validatorRequestHeaders();
    }
  }
  addBody(): void {
    this.validParameters = false;
    this.jsonBody.push({
      name: '',
      value: '',
    });
  }

  removeBody(body: any): void {
    if(this.jsonBody.length > 1) {
      this.jsonBody = this.jsonBody.filter(b => b !== body);
      this.validatorRequestBodies();
    }
  }
submit(): void {
  this.submitting = true;

  // Filter out empty or invalid entries
  const filteredRequestHeaders = this.requestHeaders.filter(header => header.name.trim() && header.value.trim());
  const filteredRequestBodies = this.jsonBody.filter(body => body.name.trim() && body.value.trim());
  const filteredRequestParameters = this.requestParameters.filter(param => param.name.trim() && param.value.trim());

  // Prepare the web service values
  const webServiceValues = this.webServiceForm.value;

  const newWebServiceConnection: any = {
    workspaceId: this.workspaceId,
    name: webServiceValues.name,
    address: webServiceValues.address,
    dataType: webServiceValues.dataType,
    methodType: webServiceValues.methodType,
    authenticationType: webServiceValues.authenticationType,
    body: 'string',
    username: webServiceValues.username,
    password: webServiceValues.password,
    requestParams: filteredRequestParameters.length > 0 ? filteredRequestParameters : [],
    requestHeaders: filteredRequestHeaders.length > 0 ? filteredRequestHeaders : [],
    jsonBody: filteredRequestBodies.length > 0 ? filteredRequestBodies : [],
  };

  // Send the request
  this.dataSourcePreviewFacade.createWebServiceConnection(
    this.workspaceId,
    webServiceValues.name,
    webServiceValues.address,
    webServiceValues.dataType,
    webServiceValues.methodType,
    webServiceValues.authenticationType,
    'string',
    webServiceValues.username,
    webServiceValues.password,
    newWebServiceConnection.requestParams,
    newWebServiceConnection.requestHeaders,
    newWebServiceConnection.jsonBody
  );
}


  closeDialog(): void {
    this.webServiceForm.reset();
    this.dialogRef.close(false);
  }



  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  validatorRequestParameters(): void {
    let invalid = false;
    for(const parameter of this.requestParameters) {
      if(parameter.name === '' || parameter.value === '') {
        invalid = true;
        break;
      }
    }
    this.validParameters = !invalid;
  }

  validatorRequestHeaders(): void {
    let invalid = false;
    for(const header of this.requestHeaders) {
      if(header.name === '' || header.value === '') {
        invalid = true;
        break;
      }
    }
    this.validParameters = !invalid;
  }

  validatorRequestBodies(): void {
    let invalid = false;
    for(const body of this.jsonBody) {
      if(body.name === '' || body.value === '') {
        invalid = true;
        break;
      }
    }
    this.validParameters = !invalid;
  }
  
  conditionalUsernameAndPasswordValidator(): void {
    const authenticationType = this.webServiceForm.value.authenticationType;
    if(authenticationType === undefined) {
      this.webServiceForm.controls.username.setValue(undefined);
      this.webServiceForm.controls.password.setValue(undefined);
      this.webServiceForm.controls.username.disable();
      this.webServiceForm.controls.password.disable();
    } else {
      this.webServiceForm.controls.username.enable();
      this.webServiceForm.controls.password.enable();
    }
  }

  changeAuthenticationTypes(event): void {
    if(event.name === 'Basic Authentication') {
      this.webServiceForm.controls['username'].addValidators([Validators.required]);
      this.webServiceForm.controls['password'].addValidators([Validators.required]);
      this.webServiceForm.controls['username'].updateValueAndValidity();
      this.webServiceForm.controls['password'].updateValueAndValidity();
    } else {
      this.webServiceForm.controls['username'].setValidators([]);
      this.webServiceForm.controls['password'].setValidators([]);
      this.webServiceForm.controls['username'].reset();
      this.webServiceForm.controls['password'].reset();
      this.webServiceForm.controls['username'].updateValueAndValidity();
      this.webServiceForm.controls['password'].updateValueAndValidity();
    }
  }
}
